/**
 * `/settings/security` — self-service security settings (Sprint-4 owed #15). Any
 * authenticated user reaches it (no role gate — it operates only on the caller's
 * OWN credentials), including a session established via recovery-code login (that
 * IS the lost-device path).
 *
 * "Re-enrol authenticator" drives the step-up rotation against worker-auth (fixtures:
 * totp-reenrol, totp-reenrol-second-factor-required, totp-confirm):
 *  1. The user re-enters their CURRENT password → POST /auth/totp/reenrol (a
 *     stolen session cookie must not suffice to swap 2FA; wrong password → the
 *     standard 401 and nothing is minted).
 *  2. The fresh PENDING secret + otpauth URI come back ONCE; the QR is rendered
 *     CLIENT-SIDE (the T-1.17 idiom — the secret is never re-sent) alongside the
 *     manual key and the issuer (worker-auth's fixed identity-plane issuer, `ARGUS`). The OLD
 *     authenticator keeps working until the new one is confirmed (zero-lockout window) —
 *     stated in the UI.
 *  3. A live 6-digit code from the NEW authenticator → POST /auth/totp/confirm
 *     activates it and invalidates the old secret atomically.
 * Recovery codes are untouched by rotation.
 *
 * Refusals handled by name (`totpRefusal`):
 *  - 401 `second_factor_required` (reenrol, or confirm) — this session was signed in with a
 *    password alone (e.g. it predates enrolment, or another tab replaced it). Changing the second
 *    factor needs a session that proved it, so the screen explains that and offers "Sign in
 *    again" → /login carrying `?redirect=/settings/security`; after the authenticator (or a
 *    recovery-code) sign-in the firm picker brings the user back here.
 *  - 409 `conflict` (confirm) — nothing is pending any more (completed or re-staged elsewhere):
 *    back to the start, with copy saying so.
 *  - 429 `account_locked` — the identity's SEC-3 failure window is exhausted. It arrives on
 *    CONFIRM (too many wrong codes) and on REENROL (too many wrong step-up passwords — the
 *    step-up counts in the very same window login uses, so it can never be an unbounded
 *    password oracle). Both get a plain "try again later" and no detail. Deliberately NOT the
 *    "Sign in again" detour: that lock holds sign-in shut too, so sending the user to /login
 *    would only walk them into another 429.
 *
 * Built on the design-system primitives (UI/UX Flow 30): one card per step. A refusal that
 * belongs to the field the user just typed in (wrong password, wrong code) is that field's
 * inline error; a refusal about the session or the lock window is a warning notice.
 */
import { useNavigate } from '@tanstack/react-router';
import { Eye, EyeOff, LogIn } from '../../components/ui/icons.ts';
import { useId, useState, type FormEvent, type JSX } from 'react';

import {
  Button,
  Card,
  CardBody,
  CardHeader,
  FieldFrame,
  Input,
  Notice,
  PageHeader,
} from '../../components/ui/index.ts';
import { api, getErrorMessage, totpRefusal, type TotpEnrolResponse } from '../../lib/api.ts';

import '../../styles/settings.css';

type Phase = 'idle' | 'starting' | 'scan' | 'confirming' | 'done';

/** Why a sign-in is needed before a two-factor change (401 `second_factor_required`). */
export const SECOND_FACTOR_REQUIRED_COPY =
  "For your security, changing two-factor authentication needs a sign-in that used your authenticator app or a recovery code. Sign in again and you'll be brought back here.";
/** Confirm refused 409 — the rotation is no longer pending. */
export const NOTHING_PENDING_COPY =
  'There is no authenticator change waiting to be confirmed — it may have been completed or restarted in another window. Start re-enrolment again if you still need to.';
/** Confirm refused 429 — the identity's failure window is exhausted. */
export const CODE_ENTRY_LOCKED_COPY =
  'Too many incorrect codes. For your security, code entry is paused for a while. Please try again later.';
/** Reenrol refused 429 — the step-up shares that same window, so wrong passwords exhaust it too. */
export const STEP_UP_LOCKED_COPY =
  'Too many incorrect passwords. For your security, this is paused for a while — signing in is paused too, so please try again later.';

/** Parse the issuer (worker-auth's fixed `ARGUS`) out of the otpauth provisioning URI. */
function parseIssuer(uri: string): string | null {
  try {
    return new URL(uri).searchParams.get('issuer');
  } catch {
    return null;
  }
}

/** Render the provisioning URI to an inline SVG QR, entirely client-side (no secret round-trip). */
async function renderQrSvg(uri: string): Promise<string> {
  const qrcode = await import('qrcode');
  return qrcode.default.toString(uri, { type: 'svg', margin: 1, width: 200 });
}

/**
 * The step-up password field: the `Input` chrome (label / error wiring) plus a reveal toggle,
 * composed on `FieldFrame`. No `autocomplete="username"` companion here, unlike `/invite/$token`:
 * this form RE-AUTHENTICATES against a credential the manager already holds from sign-in — it
 * never creates one — so there is nothing to save.
 */
function CurrentPasswordField({
  value,
  onChange,
  error,
}: {
  value: string;
  onChange: (next: string) => void;
  error: string | null;
}): JSX.Element {
  const id = useId();
  const [revealed, setRevealed] = useState(false);
  return (
    <FieldFrame id={id} label="Current password" required error={error}>
      <div className="argus-settings-password">
        <input
          id={id}
          className="argus-input"
          type={revealed ? 'text' : 'password'}
          name="current_password"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete="current-password"
          required
          aria-required="true"
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${id}-error` : undefined}
        />
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          className="argus-settings-password__reveal"
          aria-label={revealed ? 'Hide password' : 'Show password'}
          aria-pressed={revealed}
          aria-controls={id}
          onClick={() => setRevealed((r) => !r)}
          icon={
            revealed ? (
              <EyeOff size={16} aria-hidden="true" />
            ) : (
              <Eye size={16} aria-hidden="true" />
            )
          }
        />
      </div>
    </FieldFrame>
  );
}

export function SecuritySettingsScreen(): JSX.Element {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>('idle');
  const [password, setPassword] = useState('');
  const [enrol, setEnrol] = useState<TotpEnrolResponse | null>(null);
  const [qrSvg, setQrSvg] = useState<string | null>(null);
  const [code, setCode] = useState('');
  // A refusal about the value the user just typed — shown inline on that field.
  const [fieldError, setFieldError] = useState<string | null>(null);
  // A refusal about the session or the lock window — shown as a notice on the current step.
  const [notice, setNotice] = useState<string | null>(null);
  // worker-auth refused a two-factor change on this (password-only) session.
  const [needsSignIn, setNeedsSignIn] = useState(false);

  function clearErrors(): void {
    setFieldError(null);
    setNotice(null);
  }

  function dropPendingEnrolment(): void {
    setEnrol(null);
    setQrSvg(null);
    setCode('');
  }

  async function handleStart(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    clearErrors();
    setPhase('starting');
    try {
      const res = await api.auth.totpReenrol({ current_password: password });
      setPassword(''); // the step-up password is never kept around
      setEnrol(res);
      setQrSvg(await renderQrSvg(res.otpauth_uri));
      setPhase('scan');
    } catch (err) {
      switch (totpRefusal(err)) {
        case 'second_factor_required':
          // Refused before the password was even checked — it is not needed any more.
          setPassword('');
          setNeedsSignIn(true);
          break;
        case 'account_locked':
          // The step-up shares login's SEC-3 window and was refused before the password was
          // verified. Retrying now is pointless and so is signing in (the same lock holds it
          // shut) — say so and drop the password rather than offer the sign-in detour.
          setPassword('');
          setNotice(STEP_UP_LOCKED_COPY);
          break;
        default:
          setFieldError(getErrorMessage(err));
      }
      setPhase('idle');
    }
  }

  async function handleConfirm(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    clearErrors();
    setPhase('confirming');
    try {
      await api.auth.totpConfirm({ code: code.trim() });
      dropPendingEnrolment();
      setPhase('done');
    } catch (err) {
      switch (totpRefusal(err)) {
        case 'second_factor_required':
          dropPendingEnrolment();
          setNeedsSignIn(true);
          setPhase('idle');
          return;
        case 'conflict':
          dropPendingEnrolment();
          setNotice(NOTHING_PENDING_COPY);
          setPhase('idle');
          return;
        case 'account_locked':
          setNotice(CODE_ENTRY_LOCKED_COPY);
          setPhase('scan');
          return;
        default:
          // A wrong / reused code (the uniform 400) or a transport failure: retry on this step.
          setFieldError(getErrorMessage(err));
          setPhase('scan');
      }
    }
  }

  async function signInAgain(): Promise<void> {
    await navigate({ to: '/login', search: { redirect: '/settings/security' } });
  }

  const issuer = enrol ? parseIssuer(enrol.otpauth_uri) : null;

  return (
    <div className="argus-page">
      <PageHeader
        title="Security"
        description="Rotate your two-factor authenticator if you lost or replaced your device."
      />

      {phase === 'done' ? (
        <Notice tone="success">
          Authenticator re-enrolled. Your old authenticator no longer works.
        </Notice>
      ) : null}

      {needsSignIn ? (
        <Card
          className="argus-settings-form"
          role="group"
          aria-label="Sign in again to manage two-factor authentication"
        >
          <CardHeader title="Sign in again to continue" />
          <CardBody>
            <div className="stack">
              <Notice tone="warning">{SECOND_FACTOR_REQUIRED_COPY}</Notice>
              <div className="row">
                <Button
                  variant="primary"
                  icon={<LogIn size={16} aria-hidden="true" />}
                  onClick={() => void signInAgain()}
                >
                  Sign in again
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
      ) : null}

      {!needsSignIn && (phase === 'idle' || phase === 'starting' || phase === 'done') ? (
        <Card className="argus-settings-form">
          <CardHeader
            title="Re-enrol authenticator"
            description="Confirm your current password to start. Your existing authenticator keeps working until you confirm the new one — you are never locked out mid-switch."
          />
          <CardBody>
            <form onSubmit={(e) => void handleStart(e)} aria-label="Re-enrol authenticator">
              <div className="stack">
                <CurrentPasswordField
                  value={password}
                  onChange={setPassword}
                  error={phase !== 'done' ? fieldError : null}
                />
                {notice ? <Notice tone="warning">{notice}</Notice> : null}
                <div className="row">
                  <Button
                    type="submit"
                    variant="primary"
                    loading={phase === 'starting'}
                    disabled={password.length === 0}
                  >
                    Start re-enrolment
                  </Button>
                </div>
              </div>
            </form>
          </CardBody>
        </Card>
      ) : null}

      {!needsSignIn && (phase === 'scan' || phase === 'confirming') && enrol ? (
        <Card className="argus-settings-form">
          <CardHeader
            title="Scan with your new authenticator"
            description="Your OLD authenticator still works until you confirm below."
          />
          <CardBody>
            <div className="stack">
              <div className="argus-settings-qr-row">
                {qrSvg ? (
                  <div
                    className="argus-settings-qr"
                    role="img"
                    aria-label="TOTP QR code"
                    dangerouslySetInnerHTML={{ __html: qrSvg }}
                  />
                ) : null}
                <div className="stack-sm grow">
                  {issuer ? (
                    <p className="text-sm text-muted">
                      Account: <strong>{issuer}</strong>
                    </p>
                  ) : null}
                  <p className="text-sm">Or enter this key manually:</p>
                  <code className="argus-settings-key" aria-label="Manual secret key">
                    {enrol.secret}
                  </code>
                </div>
              </div>
              <form
                onSubmit={(e) => void handleConfirm(e)}
                aria-label="Confirm new authenticator code"
              >
                <div className="stack">
                  <Input
                    label="6-digit code from the new authenticator"
                    className="argus-settings-code"
                    name="totp_code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="123456"
                    mono
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    error={fieldError}
                  />
                  {notice ? <Notice tone="warning">{notice}</Notice> : null}
                  <div className="row">
                    <Button
                      type="submit"
                      variant="primary"
                      loading={phase === 'confirming'}
                      disabled={code.length !== 6}
                    >
                      Confirm new authenticator
                    </Button>
                  </div>
                </div>
              </form>
            </div>
          </CardBody>
        </Card>
      ) : null}
    </div>
  );
}
